'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';

export type IncomingChannel = 'voice' | 'livechat' | 'whatsapp' | 'email' | 'x' | 'facebook' | 'instagram';

interface IncomingNotificationData {
  channel: IncomingChannel;
  citizen: string;
  preview: string;
  sub?: string;
  topic?: string;
}

interface Props {
  data: IncomingNotificationData;
  onAccept: () => void;
  onDecline: () => void;
}

const channelConfig: Record<IncomingChannel, {
  label: string;
  headerBg: string;
  headerText: string;
  dotColor: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  acceptLabel: string;
  declineLabel: string;
}> = {
  voice: {
    label: 'VOICE CALL',
    headerBg: 'bg-green-50 border-green-200',
    headerText: 'text-green-700',
    dotColor: 'bg-green-500',
    icon: 'PhoneIcon',
    iconColor: 'text-white',
    iconBg: 'bg-primary',
    acceptLabel: 'Accept',
    declineLabel: 'Ignore',
  },
  livechat: {
    label: 'NEW LIVE CHAT',
    headerBg: 'bg-cyan-50 border-cyan-200',
    headerText: 'text-cyan-700',
    dotColor: 'bg-cyan-500',
    icon: 'ChatBubbleLeftRightIcon',
    iconColor: 'text-white',
    iconBg: 'bg-cyan-600',
    acceptLabel: 'Accept',
    declineLabel: 'Decline',
  },
  whatsapp: {
    label: 'NEW WHATSAPP MESSAGE',
    headerBg: 'bg-green-50 border-green-200',
    headerText: 'text-green-700',
    dotColor: 'bg-green-500',
    icon: 'ChatBubbleOvalLeftEllipsisIcon',
    iconColor: 'text-white',
    iconBg: 'bg-green-600',
    acceptLabel: 'Accept',
    declineLabel: 'Decline',
  },
  email: {
    label: 'NEW EMAIL',
    headerBg: 'bg-purple-50 border-purple-200',
    headerText: 'text-purple-700',
    dotColor: 'bg-purple-500',
    icon: 'EnvelopeIcon',
    iconColor: 'text-white',
    iconBg: 'bg-purple-600',
    acceptLabel: 'Open',
    declineLabel: 'Assign to Me',
  },
  x: {
    label: 'NEW X INTERACTION',
    headerBg: 'bg-slate-50 border-slate-200',
    headerText: 'text-slate-700',
    dotColor: 'bg-slate-600',
    icon: 'HashtagIcon',
    iconColor: 'text-white',
    iconBg: 'bg-slate-700',
    acceptLabel: 'Accept',
    declineLabel: 'Decline',
  },
  facebook: {
    label: 'NEW FACEBOOK MESSAGE',
    headerBg: 'bg-blue-50 border-blue-200',
    headerText: 'text-blue-700',
    dotColor: 'bg-blue-600',
    icon: 'UserGroupIcon',
    iconColor: 'text-white',
    iconBg: 'bg-blue-700',
    acceptLabel: 'Accept',
    declineLabel: 'Decline',
  },
  instagram: {
    label: 'NEW INSTAGRAM DM',
    headerBg: 'bg-pink-50 border-pink-200',
    headerText: 'text-pink-700',
    dotColor: 'bg-pink-500',
    icon: 'CameraIcon',
    iconColor: 'text-white',
    iconBg: 'bg-gradient-to-br from-pink-500 to-purple-600',
    acceptLabel: 'Accept',
    declineLabel: 'Decline',
  },
};

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

export default function IncomingInteractionNotification({ data, onAccept, onDecline }: Props) {
  const cfg = channelConfig[data.channel];

  return (
    <>
      {/* Mobile: bottom sheet overlay */}
      <div className="fixed inset-0 z-[100] pointer-events-auto sm:hidden flex flex-col justify-end">
        <div className="absolute inset-0 bg-black/40" onClick={onDecline} />
        <div className="relative bg-card rounded-t-3xl shadow-elevated overflow-hidden slide-in-up mx-0">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-border rounded-full" />
          </div>
          {/* Header strip */}
          <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${cfg.headerBg}`}>
            <div className={`w-2 h-2 rounded-full animate-pulse flex-shrink-0 ${cfg.dotColor}`} />
            <span className={`text-[12px] font-700 uppercase tracking-widest flex-1 ${cfg.headerText}`}>
              {cfg.label} · Incoming
            </span>
            <button onClick={onDecline} className="text-muted-foreground hover:text-foreground p-1">
              <Icon name="XMarkIcon" size={14} />
            </button>
          </div>
          <div className="p-4 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-[16px] font-700 flex-shrink-0 ${cfg.iconBg} ${cfg.iconColor}`}>
                {data.channel === 'voice' ? getInitials(data.citizen) : (
                  <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={22} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[18px] font-700 text-foreground">{data.citizen}</div>
                {data.sub && <div className="text-[14px] text-muted-foreground">{data.sub}</div>}
                {data.topic && <div className="text-[12px] text-muted-foreground mt-0.5 truncate">{data.topic}</div>}
              </div>
            </div>
            {data.preview && (
              <div className="bg-muted/50 rounded-xl px-4 py-3 mb-4 text-[13px] text-foreground italic border border-border">
                "{data.preview.length > 100 ? data.preview.slice(0, 100) + '...' : data.preview}"
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={onAccept}
                className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-green-500 text-white font-700 text-[15px] hover:bg-green-600 transition-colors active:scale-95"
              >
                <Icon name={data.channel === 'voice' ? 'PhoneIcon' : 'CheckIcon'} size={18} />
                {cfg.acceptLabel}
              </button>
              <button
                onClick={onDecline}
                className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-red-50 text-red-600 border border-red-200 font-700 text-[15px] hover:bg-red-100 transition-colors active:scale-95"
              >
                <Icon name={data.channel === 'voice' ? 'PhoneXMarkIcon' : 'XMarkIcon'} size={18} />
                {cfg.declineLabel}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: top-right floating notification */}
      <div className="hidden sm:block fixed top-16 right-4 z-[100] pointer-events-auto">
        <div className="w-80 bg-card border border-border rounded-2xl shadow-elevated overflow-hidden slide-in-up">
          {/* Header strip */}
          <div className={`flex items-center gap-2 px-3 py-2 border-b ${cfg.headerBg}`}>
            <div className={`w-2 h-2 rounded-full animate-pulse flex-shrink-0 ${cfg.dotColor}`} />
            <span className={`text-[11px] font-700 uppercase tracking-widest flex-1 ${cfg.headerText}`}>
              {cfg.label} · Incoming Interaction
            </span>
            <button onClick={onDecline} className="text-muted-foreground hover:text-foreground p-0.5">
              <Icon name="XMarkIcon" size={12} />
            </button>
          </div>

          <div className="p-3">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-700 flex-shrink-0 ${cfg.iconBg} ${cfg.iconColor}`}>
                {data.channel === 'voice' ? getInitials(data.citizen) : (
                  <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={18} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-700 text-foreground">{data.citizen}</div>
                {data.sub && <div className="text-[12px] text-muted-foreground">{data.sub}</div>}
                {data.topic && (
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{data.topic}</div>
                )}
              </div>
            </div>

            {data.preview && (
              <div className="bg-muted/50 rounded-xl px-3 py-2 mb-3 text-[12px] text-foreground italic border border-border">
                "{data.preview.length > 80 ? data.preview.slice(0, 80) + '...' : data.preview}"
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={onAccept}
                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-green-500 text-white font-700 text-[13px] hover:bg-green-600 transition-colors active:scale-95"
              >
                <Icon name={data.channel === 'voice' ? 'PhoneIcon' : 'CheckIcon'} size={14} />
                {cfg.acceptLabel}
              </button>
              <button
                onClick={onDecline}
                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-red-50 text-red-600 border border-red-200 font-700 text-[13px] hover:bg-red-100 transition-colors active:scale-95"
              >
                <Icon name={data.channel === 'voice' ? 'PhoneXMarkIcon' : 'XMarkIcon'} size={14} />
                {cfg.declineLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
